import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
} from '../../global/utils/storeUtils';
/**
 * The functions below, called thunks, allow us to perform async logic. They
 * can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
 * will call the thunk with the `dispatch` function as the first argument. Async
 * code can then be executed and other actions can be dispatched. Thunks are
 * typically used to make async requests.
 * 
 * In practice we won't dispatch these directly, they will be dispatched by userEventService which has a nicer api built on hooks.
 */

// CREATE
export const sendCreateUserEvent = createAsyncThunk(
  'userEvent/sendCreate'
  , async (newUserEvent) => {
    const endpoint = `/api/user-events`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newUserEvent);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// READ
export const fetchSingleUserEvent = createAsyncThunk(
  'userEvent/fetchSingle'
  , async (id) => {
    const endpoint = `/api/user-events/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchUserEventList = createAsyncThunk(
  'userEvent/fetchList' // this is the action name that will show up in the console logger.
  , async (listArgs) => {
    const endpoint = `/api/user-events${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// for each resource we can add as many endpoints as we want in this format and we only need two actions to handle them.
export const fetchSingleUserEventAtEndpoint = createAsyncThunk(
  'userEvent/fetchSingleWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/user-events${query}` // example: `/api/user-events/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchUserEventListAtEndpoint = createAsyncThunk(
  'userEvent/fetchListWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    // catch for the redstone prefix and call the correct api function
    const callAPI = query.includes('/redstone') ? apiUtils.callRedstoneAPI : apiUtils.callAPI;
    const endpoint = `/api/user-events${query}`; // example: `/api/user-events/logged-in?${queryString}`
    const response = await callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// UPDATE
export const sendUpdateUserEvent = createAsyncThunk(
  'userEvent/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/user-events/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// DELETE
export const sendDeleteUserEvent = createAsyncThunk(
  'userEvent/sendDelete'
  , async (id) => {
    const endpoint = `/api/user-events/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);


// next define the store's initial state, all of our store utils rely on a specific state shape, so use the constant
const initialState = { ...INITIAL_STATE };

// define the userEventSlice. This is a combination of actions and reducers. More info: https://redux-toolkit.js.org/api/createSlice
export const userEventSlice = createSlice({
  name: 'userEvent'
  , initialState
  /**
   * The `reducers` field lets us define reducers and generate associated actions.
   * Unlike the selectors defined at the bottom of this file, reducers only have access
   * to this specific reducer and not the entire store.
   * 
   * Again, we will not dispatch these directly, they will be dispatched by userEventService.
   */
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addUserEventToList: handleAddSingleToList
  }

  /**
   * The `extraReducers` field lets the slice handle actions defined elsewhere,
   * including actions generated by createAsyncThunk or in other slices.
   * We'll use them to track our server request status.
   * 
   * We'll add a case for each API call defined at the top of the file to dictate
   * what happens during each API call lifecycle.
   */
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateUserEvent.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleUserEvent.pending, handleFetchSinglePending)
      .addCase(fetchSingleUserEvent.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleUserEvent.rejected, handleFetchSingleRejected)
      .addCase(fetchUserEventList.pending, handleFetchListPending)
      // because lists are returned from the server named for their resource, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchUserEventList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'userEvents'))
      .addCase(fetchUserEventList.rejected, handleFetchListRejected)
      // permission protected single fetches
      .addCase(fetchSingleUserEventAtEndpoint.pending, handleFetchSinglePending)
      // these endpoints return named lists, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchSingleUserEventAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'userEvents'))
      .addCase(fetchSingleUserEventAtEndpoint.rejected, handleFetchSingleRejected)
      // permission protected list fetches
      .addCase(fetchUserEventListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchUserEventListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'userEvents'))
      .addCase(fetchUserEventListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateUserEvent.pending, handleMutationPending)
      .addCase(sendUpdateUserEvent.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateUserEvent.rejected, handleMutationRejected)
      // .addCase(sendUpdateUserEvent.fulfilled, (state, action) => handleMutationFulfilled(state, action, (newState, action) => {
      //   // by passing this optional callback we now have access to the new state if we want to do something else with it, this works for all reducer handlers
      // }))

      // DELETE
      .addCase(sendDeleteUserEvent.pending, handleDeletePending)
      .addCase(sendDeleteUserEvent.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteUserEvent.rejected, handleDeleteRejected)
  }
});

// export the actions for the reducers defined above
export const { invalidateQuery, invalidateQueries, addUserEventToList } = userEventSlice.actions;


// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// updated to accept a listFetch function so we can use it for other list fetches (like public landing page userEvents)
export const fetchListIfNeeded = (queryKey, listFetch = fetchUserEventList) => (dispatch, getState) => {
  const userEventQuery = getState().userEvent.listQueries[queryKey];
  if(shouldFetch(userEventQuery)) {
    // console.log('Fetching userEvent list', queryKey);
    dispatch(listFetch(queryKey));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
};

export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleUserEvent) => (dispatch, getState) => {
  const userEventQuery = getState().userEvent.singleQueries[id];
  if(shouldFetch(userEventQuery)) {
    dispatch(singleFetch(id));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
}

export default userEventSlice.reducer;
